import React, { useState, useEffect } from "react";
import { Search, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Panel } from "../../lib/ui";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";

export const num = (n) => (n || 0).toLocaleString("id-ID");
export const platformBadge = (p) => (
  <Badge variant="outline" className="capitalize border-cocoa/30 text-cocoa">{p}</Badge>
);
export const timeStr = (ms) => (ms ? new Date(ms).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) : "-");

export const BUCKETS = ["HOME", "STORE", "PRE_ORDER"];
export const PLATFORM_FILTER = {
  key: "platform", label: "Platform", allLabel: "Semua platform",
  options: [{ value: "shopee", label: "Shopee" }, { value: "tiktok", label: "Tiktok" }],
  match: (r, v) => (r.platform || "").toLowerCase() === v,
};

export const keepStatusBadge = (s) => {
  if (s === "created") return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Berhasil</Badge>;
  if (s === "failed") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Gak bisa dikeep</Badge>;
  if (s === "resolved") return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Sudah ditangani</Badge>;
  if (s === "cancelled") return <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100">Dibatalkan pembeli</Badge>;
  return <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Skip</Badge>;
};

const toneMap = { cocoa: "text-cocoa", green: "text-emerald-600", amber: "text-amber-600", slate: "text-slate-500" };
export function StatCard({ label, value, tone }) {
  return (
    <Panel className="p-4" data-testid={`desty-stat-${label}`}>
      <div className="text-xs text-cocoa/60">{label}</div>
      <div className={`text-2xl font-bold ${toneMap[tone] || "text-cocoa"}`}>{num(value)}</div>
    </Panel>
  );
}

export function TableSkeleton({ rows = 6, testid, message = "Menarik data dari Desty… (bisa 20–40 detik)" }) {
  return (
    <Panel className="p-4 space-y-3" data-testid={testid}>
      <div className="flex gap-2">
        <div className="h-10 flex-1 rounded-md bg-cocoa/10 animate-pulse" />
        <div className="h-10 w-40 rounded-md bg-cocoa/10 animate-pulse" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-9 rounded-md bg-cocoa/5 animate-pulse" style={{ animationDelay: `${i * 70}ms` }} />
      ))}
      <p className="text-center text-xs text-cocoa/50 pt-1">{message}</p>
    </Panel>
  );
}

export function TabBar({ tabs, value, onChange, prefix }) {
  return (
    <div className="flex gap-2 border-b border-cocoa/10 flex-wrap">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button key={t.key} onClick={() => onChange(t.key)} data-testid={`${prefix}-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${value === t.key ? "border-cocoa text-cocoa font-semibold" : "border-transparent text-cocoa/50 hover:text-cocoa"}`}>
            {Icon && <Icon className="w-4 h-4" />} {t.label}{t.count != null && <span className="text-xs opacity-70">({t.count})</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Reusable data table: pencarian teks, filter dropdown, sort kolom, dan pagination (client-side). */
export function DataTable({ columns, rows, searchAccessors = [], searchPlaceholder = "Cari…", filters = [], emptyText = "Tidak ada data.", testid, rightSlot, rowTestId, pageSize = 15, server, mobileCards = false }) {
  const stacked = !!server || mobileCards;
  const [localQ, setLocalQ] = useState("");
  const [localFvals, setLocalFvals] = useState({});
  const [localPage, setLocalPage] = useState(1);
  const [localSort, setLocalSort] = useState({ key: null, dir: "asc" });
  const q = server ? server.q : localQ;
  const setQ = server ? server.setQ : setLocalQ;
  const fvals = server ? server.filters : localFvals;
  const setFvals = server ? server.setFilters : setLocalFvals;
  const page = server ? server.page : localPage;
  const setPage = server ? server.setPage : setLocalPage;
  const sort = server ? { key: server.sort.sort, dir: server.sort.dir } : localSort;
  useEffect(() => { setLocalPage(1); }, [q, fvals, rows]);

  const norm = (v) => String(v ?? "").toLowerCase();
  let out = rows || [];
  const term = q.trim().toLowerCase();
  if (!server && term && searchAccessors.length) out = out.filter((r) => searchAccessors.some((f) => norm(f(r)).includes(term)));
  if (!server) filters.forEach((f) => {
    const val = fvals[f.key] ?? "all";
    if (val !== "all") out = out.filter((r) => f.match(r, val));
  });
  if (!server && sort.key) {
    const col = columns.find((c) => c.key === sort.key);
    const acc = col?.sortAccessor;
    if (acc) {
      out = [...out].sort((a, b) => {
        const av = acc(a), bv = acc(b);
        const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : norm(av).localeCompare(norm(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
  }
  const total = server ? server.total : out.length;
  const pages = server ? server.pages : Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(page, pages);
  const slice = server ? out : out.slice((cur - 1) * pageSize, cur * pageSize);
  const toggleSort = (key) => server ? server.toggleSort(key) : setLocalSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2.5 top-3 text-cocoa/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} className="pl-8 bg-white" data-testid={`${testid}-search`} />
        </div>
        {filters.map((f) => (
          <Select key={f.key} value={fvals[f.key] || "all"} onValueChange={(v) => setFvals((s) => ({ ...s, [f.key]: v }))}>
            <SelectTrigger className="w-40 bg-white" data-testid={`${testid}-filter-${f.key}`}><SelectValue placeholder={f.label} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid={`${testid}-filter-${f.key}-all`}>{f.allLabel || `Semua ${f.label}`}</SelectItem>
              {f.options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-filter-${f.key}-${o.value}`}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        {rightSlot}
      </div>
      <Panel className="max-w-full overflow-x-auto" data-testid={testid}>
        {stacked && <div className="flex flex-wrap gap-2 p-3 md:hidden" data-testid={`${testid}-mobile-sort`}>
          {columns.filter((c) => c.sortAccessor).map((c) => <Button key={c.key} size="sm" variant="ghost"
            onClick={() => toggleSort(c.key)} data-testid={`${testid}-mobile-sort-${c.key}`}>
            {c.header}<ArrowUpDown className="ml-1 w-3 h-3" />
          </Button>)}
        </div>}
        <Table className={stacked ? "block md:table" : ""}>
          <TableHeader className={stacked ? "hidden md:table-header-group" : ""}>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={c.align === "right" ? "text-right" : ""}>
                  {c.sortAccessor ? (
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-cocoa" data-testid={`${testid}-sort-${c.key}`}>
                      {c.header} <ArrowUpDown className={`w-3 h-3 ${sort.key === c.key ? "text-cocoa" : "opacity-40"}`} />
                    </button>
                  ) : c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className={stacked ? "block md:table-row-group" : ""}>
            {(server?.loading || server?.error || slice.length === 0) && <TableRow><TableCell colSpan={columns.length} className="text-center text-cocoa/50 py-8" data-testid={`${testid}-state`}>
              {server?.loading ? "Memuat…" : server?.error ? "Gagal memuat data. Periksa filter atau muat ulang." : emptyText}
            </TableCell></TableRow>}
            {!server?.loading && !server?.error && slice.map((row, i) => (
              <TableRow key={row.order_sn || row.id || i} className={stacked ? "block p-3 space-y-2 md:table-row md:p-0 md:space-y-0" : ""} data-testid={rowTestId ? rowTestId(row) : (row.order_sn ? `${testid}-row-${row.order_sn}` : undefined)}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={`${stacked ? "block p-0 whitespace-normal [overflow-wrap:anywhere] md:table-cell md:p-4" : ""} ${c.align === "right" ? (stacked ? "md:text-right" : "text-right") : ""} ${c.className || ""}`}>
                    {stacked && c.header && <span className="block text-xs text-cocoa/50 md:hidden">{c.header}</span>}
                    {c.render ? c.render(row) : row[c.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      {(server || total > pageSize) && (
        <div className="flex flex-wrap gap-2 items-center justify-between text-sm text-cocoa/60">
          <span data-testid={`${testid}-count`}>Menampilkan {total === 0 ? 0 : (cur - 1) * pageSize + 1}–{Math.min(cur * pageSize, total)} dari {total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(cur - 1)} disabled={cur <= 1 || server?.loading} data-testid={`${testid}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
            <span data-testid={`${testid}-page`}>Hal {cur}/{pages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(cur + 1)} disabled={cur >= pages || server?.loading} data-testid={`${testid}-next`}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
